import { Modal, Switch, Table } from "antd";
import { IInstitution, InstitutionEmail, updateInstitution } from "../../redux/reducer/institution";
import { useCallback, useMemo, useState } from "react";
import { ColumnsType } from "antd/es/table";
import { camelCaseToCapitalized } from "../../utils/stringHelpers";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../configureStore";
import { useMsal } from "@azure/msal-react";
import { EditOutlined } from "@ant-design/icons";
import EmailConfigurationForm from "../molecules/EmailConfigurationForm";

interface EmailConfigurationParams {
    institution: IInstitution;
}

const EmailConfiguration = ({
    institution,
}: EmailConfigurationParams) => {
    const { accounts } = useMsal();
    const dispatch = useDispatch<AppDispatch>();
    const loading = useSelector<RootState, boolean>(({ status: { loading: l } }) => (l['institution/PATCH_INSTITUTION']));
    const [activeIndex, setActiveIndex] = useState<number>(-1);

    const update = useCallback((index: number, record: InstitutionEmail, successMessage?: string, onSuccess?: () => void) => {
        const copied = structuredClone(institution);
        copied.emails[index] = record;

        dispatch(updateInstitution({
            input: copied,
            message: { success: successMessage },
            headers: {
                Authorization: `Bearer ${accounts[0].idToken}`,
            },
            onSuccess,
        }))
    }, [institution])

    const columns = useMemo<ColumnsType<InstitutionEmail>>(() => ([
        {
            title: 'Type',
            dataIndex: 'type',
            render: camelCaseToCapitalized
        },
        {
            title: 'Enabled',
            dataIndex: 'enabled',
            render: (enabled: boolean, record: InstitutionEmail, index: number) => (
                <Switch
                    checked={enabled}
                    onClick={(checked) => {
                        update(index, { ...record, enabled: checked }, `${camelCaseToCapitalized(record.type)} email ${checked ? 'enabled' : 'disabled'}!`);
                    }}
                    checkedChildren="Enabled"
                    unCheckedChildren="Disabled"
                    loading={loading}
                />
            ),
        },
        {
            title: 'White Label',
            dataIndex: 'emailWhiteLabel',
            render: (_wl: InstitutionEmail['emailWhiteLabel'], _record: InstitutionEmail, index: number) => (
                <EditOutlined
                    onClick={() => { setActiveIndex(index) }}
                />
            )
        }
    ]), [institution, loading]);

    return (
        <>
            <Modal
                open={activeIndex >= 0}
                onCancel={() => { setActiveIndex(-1); }}
                keyboard={false}
                footer={null}
                title={camelCaseToCapitalized(institution.emails?.[activeIndex]?.type)}
                width={840}
            >
                {activeIndex >= 0 && (
                    <EmailConfigurationForm
                        institutionEmail={institution.emails[activeIndex]}
                        onFinish={(record) => {
                            update(
                                activeIndex,
                                { ...institution.emails[activeIndex], ...record },
                                `${camelCaseToCapitalized(institution.emails?.[activeIndex]?.type)} email updated!`,
                                () => { setActiveIndex(-1); },
                            );
                        }}
                    />
                )}
            </Modal>
            <Table
                dataSource={institution.emails ?? []}
                columns={columns}
            />
        </>
    );
};

export default EmailConfiguration;
